import { action, computed, observable, reaction, makeObservable } from 'mobx';
import {User} from '../../../domain';
import {Disposers, toastrHelper} from '../../../lib/helpers';
import {Classroom, ClassroomRole} from '../../classroom/models/classroom-models';
import {ClassroomAddStore} from './parts/ClassroomAddStore';
import {ClassroomDeleteStore} from './parts/ClassroomDeleteStore';
import {ClassroomEditFormStore} from './parts/ClassroomEditFormStore';
import {ClassroomEditStore} from './parts/ClassroomEditStore';
import {ClassroomSearchStore} from './parts/ClassroomSearchStore';
import {ContentLookupStore} from './parts/ContentLookupStore';
import {UserSearchStore} from './parts/UserSearchStore';
import {ChangeTimeSuggestionStore} from './parts/ChangeTimeSuggestionStore';

export class AdminClassroomSceneStore {
  @observable
  mode: 'view' | 'add-classroom' | 'edit-classroom' | 'delete-classroom';

  readonly contentLookup: ContentLookupStore;
  readonly studentSearch: UserSearchStore;
  readonly teacherSearch: UserSearchStore;
  readonly classroomSearch: ClassroomSearchStore;

  @observable.ref
  addClassroom: ClassroomAddStore | null;
  @observable.ref
  editClassroom: ClassroomEditStore | null;
  @observable.ref
  deleteClassroom: ClassroomDeleteStore | null;

  private readonly disposers = new Disposers();

  constructor() {
    makeObservable(this);
    this.mode = 'view';

    this.contentLookup = new ContentLookupStore();
    this.studentSearch = new UserSearchStore(this, 'Student');
    this.teacherSearch = new UserSearchStore(this, 'Teacher');
    this.classroomSearch = new ClassroomSearchStore();
    this.disposers.add(this.studentSearch, this.teacherSearch, this.classroomSearch);

    this.addClassroom = this.editClassroom = this.deleteClassroom = null;



    this.setup();
  }

  @computed
  get currentEditForm(): ClassroomEditFormStore | null {
    if (this.mode === 'add-classroom')
      return this.addClassroom!.form;
    if (this.mode === 'edit-classroom')
      return this.editClassroom!.form;
    return null;
  }

  editFormUserId(role: ClassroomRole): number {
    const editForm = this.currentEditForm;
    if (editForm == null)
      return -1;
    if (role === 'Teacher' && editForm.teacher.value !== null)
      return editForm.teacher.value.Id;
    if (role === 'Student' && editForm.student.value !== null)
      return editForm.student.value.Id;
    return -1;
  }

  editFormDateTime(): number | null {
    const editForm = this.currentEditForm;
    if (editForm == null)
      return null;
    return editForm.startTime.value;
  }

  @action
  pickUserFromSearch = (role: ClassroomRole) => {
    if (role === 'Teacher') {
      const teacher = this.teacherSearch.selectUser;
      this.pickTeacher(teacher);
    }
    if (role === 'Student') {
      const student = this.studentSearch.selectUser;
      this.pickStudent(student);
    }
  };

  @action
  pickStudent(student: User | null) {
    if (this.currentEditForm === null)
      return;
    this.currentEditForm.setStudent(student);
  }

  @action
  pickTeacher(teacher: User | null) {
    if (this.currentEditForm === null)
      return;
    this.currentEditForm.setTeacher(teacher);
  }

  @action
  pickLessonFromClassroom = (classroom: Classroom) => {
    if (this.currentEditForm === null)
      return;
    this.currentEditForm.setLesson(classroom.Lesson);
  };

  @action
  pickDateTimeFromClassroom = (classroom: Classroom) => {
    if (this.currentEditForm === null)
      return;
    this.currentEditForm.setStartTime(classroom.ScheduledStartTime);
  };

  /****************************************************************************/

  @action
  beginAdd = () => {
    if (this.contentLookup.lookup === null)
      return;
    this.mode = 'add-classroom';
    this.addClassroom = new ClassroomAddStore(this, this.contentLookup.lookup);
  };

  @action
  completeAdd = () => {
    this.addClassroom?.complete();
  };

  /****************************************************************************/

  @action
  beginEdit = (classroom: Classroom) => {
    if (this.contentLookup.lookup === null)
      return;
    this.mode = 'edit-classroom';
    this.editClassroom = new ClassroomEditStore(this, classroom, this.contentLookup.lookup);
  };

  @action
  completeEdit = () => {
    this.editClassroom?.complete();
  };

  /****************************************************************************/

  @action
  beginDelete(classroom: Classroom) {
    this.mode = 'delete-classroom';
    this.deleteClassroom = new ClassroomDeleteStore(this, classroom);
    this.completeDelete();
  }

  @action
  completeDelete = () => {
    this.deleteClassroom?.complete();
  };

  /****************************************************************************/

  @action
  private toViewMode() {
    this.mode = 'view';
    this.addClassroom?.dispose();
    this.editClassroom?.dispose();
    this.addClassroom = this.editClassroom = this.deleteClassroom = null;
  }

  @action
  cancel = () => {
    if (this.addClassroom?.save?.isPending === true ||
      this.editClassroom?.save?.isPending === true ||
      this.deleteClassroom?.save?.isPending === true)
      return;

    this.toViewMode();
  };

  private setup() {
    this.disposers.add(
      reaction(
        () => [this.studentSearch.classroomFilter, this.teacherSearch.classroomFilter],
        () => {
          this.classroomSearch.studentFilter = this.studentSearch.classroomFilter;
          this.classroomSearch.teacherFilter = this.teacherSearch.classroomFilter;
        }),
      reaction(
        () => [
          this.addClassroom?.save?.state,
          this.editClassroom?.save?.state,
          this.deleteClassroom?.save?.state],
        this.reloadOnSuccess
      ));
    };

  @action
  private reloadOnSuccess = () => {
    for (let state of [
      this.addClassroom?.save,
      this.editClassroom?.save,
      this.deleteClassroom?.save])
      if (state?.isResolved === true) {
        toastrHelper.success(state.value);
        this.toViewMode();
        if (this.classroomSearch?.resultState !== null)
          this.classroomSearch.searchClassrooms();
      }
  };

  dispose = () => {
    this.toViewMode();
    this.disposers.dispose();
  };
}
